import { PRODUCTS } from '#pages/data';
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';
import { FaCheckCircle } from 'react-icons/fa/index.js';
import {
  MdAutoAwesome,
  MdDashboardCustomize,
  MdPsychology,
  MdCloud,
} from 'react-icons/md/index.js';

const PRODUCT_ICONS = [
  MdAutoAwesome,
  MdPsychology,
  MdDashboardCustomize,
  MdCloud,
];

const MotionBox = motion(Box);

const ProductShowcase = () => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.100', 'gray.700');
  const subText = useColorModeValue('gray.600', 'gray.400');
  const priceColor = useColorModeValue('gray.900', 'white');
  const priceBg = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box py={{ base: 16, md: 24 }}>
      <Container maxW="container.xl">
        <VStack spacing={4} mb={16} textAlign="center">
          <Badge
            colorScheme="blue"
            fontSize="sm"
            px={4}
            py={1}
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="widest"
          >
            Our Solutions
          </Badge>
          <Heading
            as="h2"
            fontSize={{ base: '3xl', md: '4xl' }}
            fontWeight="extrabold"
            letterSpacing="-0.03em"
          >
            Pick the process you want eliminated.
          </Heading>
          <Text fontSize="lg" color={subText} maxW="xl">
            Each solution is a complete, end-to-end implementation — not a
            strategy deck. You leave with something that works.
          </Text>
        </VStack>

        <SimpleGrid
          columns={{ base: 1, lg: 2, xl: 4 }}
          spacing={8}
        >
          {PRODUCTS.map((product, i) => (
            <MotionBox
              key={product.id}
              flex="1"
              bg={bg}
              border="1px solid"
              borderColor={border}
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="lg"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 } as any}
              _hover={{ boxShadow: '2xl', transform: 'translateY(-4px)' }}
              style={{ transition: 'all 0.3s ease' }}
            >
              {/* Gradient Header */}
              <Box
                bgGradient={product.gradient}
                p={8}
                pb={6}
              >
                <Icon
                  as={PRODUCT_ICONS[i]}
                  boxSize={10}
                  color="white"
                  mb={4}
                  display="block"
                />
                <Heading
                  as="h3"
                  fontSize="xl"
                  fontWeight="bold"
                  color="white"
                  mb={2}
                >
                  {product.name}
                </Heading>
                <Text fontSize="sm" color="whiteAlpha.800">
                  {product.tagline}
                </Text>
              </Box>

              {/* Body */}
              <VStack spacing={6} p={8} align="stretch">
                <Text fontSize="sm" color={subText} lineHeight="1.7">
                  {product.description}
                </Text>

                {/* Outcomes */}
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color={subText}
                    mb={3}
                  >
                    What changes for you
                  </Text>
                  <List spacing={2}>
                    {product.outcomes.map((o) => (
                      <ListItem key={o} fontSize="sm" display="flex" alignItems="center">
                        <ListIcon as={FaCheckCircle} color="green.400" />
                        {o}
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {/* Price */}
                <Flex
                  align="center"
                  justify="space-between"
                  bg={priceBg}
                  borderRadius="lg"
                  p={4}
                  mt="auto"
                >
                  <Box>
                    <Text fontSize="xs" color={subText} fontWeight="medium">
                      Investment
                    </Text>
                    <Text
                      fontSize="lg"
                      fontWeight="extrabold"
                      color={priceColor}
                    >
                      {product.priceRange}
                    </Text>
                    <Text fontSize="xs" color={subText}>
                      {product.billingNote}
                    </Text>
                  </Box>
                  <Button
                    as="a"
                    href={product.href}
                    colorScheme="blue"
                    size="sm"
                    px={5}
                    borderRadius="lg"
                    _hover={{ transform: 'translateX(2px)' }}
                    transition="all 0.2s"
                  >
                    Learn More →
                  </Button>
                </Flex>
              </VStack>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default ProductShowcase;
